import {useMemo} from 'react';

import {useMarketingSplitCardsCarouselV1Query} from 'src/generated/graphql';

import arSAData from '../mocks/ar-SA/ar-SA.json';
import deDEData from '../mocks/de-DE/de-DE.json';
import enUSData from '../mocks/en-US/en-US.json';
import esESData from '../mocks/es-ES/es-ES.json';
import frFRData from '../mocks/fr-FR/fr-FR.json';
import idIDData from '../mocks/id-ID/id-ID.json';
import itITData from '../mocks/it-IT/it-IT.json';
import jaJPData from '../mocks/ja-JP/ja-JP.json';
import koKRData from '../mocks/ko-KR/ko-KR.json';
import nlNLData from '../mocks/nl-NL/nl-NL.json';
import plPLData from '../mocks/pl-PL/pl-PL.json';
import ptBRData from '../mocks/pt-BR/pt-BR.json';
import roROData from '../mocks/ro-RO/ro-RO.json';
import ruRUData from '../mocks/ru-RU/ru-RU.json';
import thTHData from '../mocks/th-TH/th-TH.json';
import trTRData from '../mocks/tr-TR/tr-TR.json';
import viVNData from '../mocks/vi-VN/vi-VN.json';
import zhCNData from '../mocks/zh-CN/zh-CN.json';
import zhHantTWData from '../mocks/zh-Hant-TW/zh-Hant-TW.json';

// Map locale codes to their corresponding data
// Support both underscore format (en_US) and hyphen format (en-US)
const localeDataMap = {
    'en-US': enUSData,
    en_US: enUSData,
    'ar-SA': arSAData,
    ar_SA: arSAData,
    'zh-CN': zhCNData,
    zh_CN: zhCNData,
    'zh-Hant-TW': zhHantTWData,
    zh_Hant_TW: zhHantTWData,
    'nl-NL': nlNLData,
    nl_NL: nlNLData,
    'fr-FR': frFRData,
    fr_FR: frFRData,
    'de-DE': deDEData,
    de_DE: deDEData,
    'id-ID': idIDData,
    id_ID: idIDData,
    'it-IT': itITData,
    it_IT: itITData,
    'ja-JP': jaJPData,
    ja_JP: jaJPData,
    'ko-KR': koKRData,
    ko_KR: koKRData,
    'pl-PL': plPLData,
    pl_PL: plPLData,
    'pt-BR': ptBRData,
    pt_BR: ptBRData,
    'ro-RO': roROData,
    ro_RO: roROData,
    'ru-RU': ruRUData,
    ru_RU: ruRUData,
    'es-ES': esESData,
    es_ES: esESData,
    'th-TH': thTHData,
    th_TH: thTHData,
    'tr-TR': trTRData,
    tr_TR: trTRData,
    'vi-VN': viVNData,
    vi_VN: viVNData,
} as const;

type LocaleCode = keyof typeof localeDataMap;

// Hook to load locale-specific contentful data for the enriched skills component (legacy - using mock data)
export const useLocaleContentfulData = (locale: string) => {
    return useMemo(() => {
        return localeDataMap[locale as LocaleCode] || localeDataMap['en_US'];
    }, [locale]);
};

// Hook to load marketing split cards carousel data from GraphQL API with locale fallback
export const useMarketingSplitCardsData = (locale?: string) => {
    const {data, isLoading, error} = useMarketingSplitCardsCarouselV1Query();

    // Fallback to mock data if GraphQL fails or for unsupported locales
    const mockData = useMemo(() => {
        if (locale) {
            return getLocaleContentfulData(locale);
        }
        return null;
    }, [locale]);

    return useMemo(() => {
        const graphqlData = data?.marketingSplitCardsCarouselV1;
        const hasGraphqlData = graphqlData && graphqlData.cards && graphqlData.cards.length > 0;

        let header: {headline?: string; supportingText?: string | null} | null;
        let cards: Array<{title: string; url: string; image: {url: string}}>;

        if (hasGraphqlData) {
            // Use GraphQL marketing data
            header = graphqlData.header;
            cards = graphqlData.cards
                .filter((card): card is NonNullable<typeof card> => Boolean(card))
                .map((card) => ({
                    title: card.title,
                    url: card.url,
                    image: {url: card.image?.url || ''},
                }));
        } else if (mockData && !graphqlData) {
            // Fall back to mock data when GraphQL data is not available
            const mockHeader = mockData.data.largeCardCarouselCollection.items[0];
            header = {
                headline: mockHeader.title,
                supportingText: mockHeader.description,
            };
            cards = mockHeader.skillCardCollection.items.map((item) => ({
                title: item.title,
                url: item.url,
                image: {url: item.image.url},
            }));
        } else {
            // No data available
            header = null;
            cards = [];
        }

        return {
            data: graphqlData,
            isLoading,
            error,
            header,
            cards,
            // Keep these for backward compatibility if needed
            mockData,
            hasFallback: !!mockData && !graphqlData,
        };
    }, [data, isLoading, error, mockData]);
};

// Get the contentful data for a specific locale (non-hook version for server-side usage)
export const getLocaleContentfulData = (locale: string) => {
    return localeDataMap[locale as LocaleCode] || localeDataMap['en_US'];
};
